import React, { useState } from 'react';
import { Card, Typography, Button, Space, Tag, Tooltip } from 'antd';
import { CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons';
import type { CardProps } from 'antd/lib/card';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
  cardStyle: {
    width: '100%',
    marginBottom: '10px',
  },
  iconStyle: {
    fontSize: '14px',
    marginLeft: '5px',
  },
  heading: {
    display: 'inline-block',
    fontSize: '18px !important',
    marginBottom: '5px !important',
  },
  subHeading: {
    display: 'block',
    marginRight: '90px',
    whiteSpace: 'normal',
    fontSize: '12px',
    marginTop: -5,
  },
  headingCount: {
    fontSize: '16px',
    color: '#687078 !important',
    marginLeft: '5px',
  },
});

const { Text, Title } = Typography;

export interface ConfigurationCardProps extends CardProps {
  heading: string;
  subHeading?: string;
  headingCount?: number;
  collapsable?: boolean;
  collapsed?: boolean;
  archived?: boolean;
  readonly?: boolean;
  addAction?: () => void;
  editAction?: () => void;
  deleteAction?: () => void;
  exportAction?: () => void;
  selectedId?: string | null;
  selectedIdType?: string;
}

export const ConfigurationCard = ({
  children,
  heading,
  subHeading,
  headingCount,
  collapsable,
  collapsed,
  readonly,
  addAction,
  editAction,
  deleteAction,
  exportAction,
  archived,
  selectedIdType,
  selectedId,
  ...rest
}: ConfigurationCardProps) => {
  const classes = useStyles();
  const [isCollapsed, setCollapsed] = useState(collapsed);
  const headingCountEl = headingCount ? <span className={classes.headingCount}>({headingCount})</span> : undefined;
  const icon = isCollapsed ? (
    <CaretRightOutlined className={classes.iconStyle} />
  ) : (
    <CaretDownOutlined className={classes.iconStyle} />
  );
  const title = collapsable ? (
    <Title className={classes.heading} level={5} onClick={() => setCollapsed(!isCollapsed)}>
      {heading}
      {headingCountEl}
      {icon}
    </Title>
  ) : (
    <Title className={classes.heading} level={5}>
      {heading}
      {headingCountEl}
    </Title>
  );
  const titleDescription = subHeading ? (
    <div>
      {title}
      <Text className={classes.subHeading} type={'secondary'}>
        {subHeading}
      </Text>
    </div>
  ) : (
    title
  );
  const bodyStyle = collapsable === true && isCollapsed === true ? { padding: 0 } : rest.bodyStyle;
  const actions: React.ReactNode[] = [];
  const selectedDisabled = selectedId ? false : true;
  const disabledTooltipWrapper = (el: React.ReactElement, action: string) => {
    const selectedType = selectedIdType ? selectedIdType : 'row';
    if (selectedDisabled) {
      return <Tooltip title={`You must select a ${selectedType} to ${action}.`}>{el}</Tooltip>;
    } else {
      return el;
    }
  };

  if (typeof editAction === 'function' && !archived && !readonly) {
    actions.push(
      disabledTooltipWrapper(
        <Button size={'small'} onClick={() => editAction()} disabled={selectedDisabled}>
          Edit
        </Button>,
        'edit',
      ),
    );
  }
  if (typeof deleteAction === 'function' && !archived && !readonly) {
    actions.push(
      disabledTooltipWrapper(
        <Button size={'small'} onClick={() => deleteAction()} disabled={selectedDisabled}>
          Delete
        </Button>,
        'delete',
      ),
    );
  }
  if (typeof exportAction === 'function' && !archived && !readonly) {
    actions.push(
      disabledTooltipWrapper(
        <Button size={'small'} onClick={() => exportAction()} disabled={selectedDisabled}>
          Export
        </Button>,
        'export',
      ),
    );
  }
  if (typeof addAction === 'function' && !archived && !readonly) {
    actions.push(
      <Button size={'small'} onClick={() => addAction()}>
        Add
      </Button>,
    );
  }

  const extra = readonly ? <Tag>Read-Only</Tag> : <Space>{actions}</Space>;
  return (
    <Card
      title={titleDescription}
      type={'inner'}
      size="small"
      className={classes.cardStyle}
      extra={extra}
      bodyStyle={bodyStyle}
      {...rest}
    >
      <div hidden={collapsable === true && isCollapsed === true}>{children}</div>
    </Card>
  );
};
